import { CarePlanGoalResponse } from '~/lib/types/carePlan/carePlans';
import {
  ChakraBox,
  ChakraFlex,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  ModalV2,
  Text,
  TextInputV2,
  useToastMessage,
} from '@equip.health/ui';
import { useUpdateGoalMutation } from '~/store/services/carePlan';
import { useEffect, useState } from 'react';
import { CarePlanGoalStatus } from '~/lib/types/carePlan/CarePlanGoalStatus';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';

const TOP_LEVEL_ID = 'edit-goal-modal';
type Props = {
  goal: CarePlanGoalResponse;
  treatmentEpisodeId: string;
  onClose: () => void;
};
export const EditGoalModal = ({ treatmentEpisodeId, goal, onClose }: Props) => {
  const { trackEvent } = useAnalytics();
  const [
    updateGoal,
    { isLoading: isUpdateGoalLoading, isError: isUpdateGoalError },
  ] = useUpdateGoalMutation();
  const { createToast } = useToastMessage();

  const [description, setDescription] = useState('');

  useEffect(() => {
    if (isUpdateGoalError) {
      createToast({
        icon: FAIconName.exclamationTriangleRegular,
        id: `${TOP_LEVEL_ID}_error-toast`,
        text: "We're sorry, we were unable to update this goal. Please try again later",
        variant: 'critical',
      });
      onClose();
    }
  }, [isUpdateGoalError]);

  return (
    <ModalV2
      isOpen
      onClose={() => {
        trackEvent(CarePlanEvents.clickOnCloseIconInEditGoalModal);
        onClose();
      }}
      size="4xl"
      title="Edit goal"
    >
      <ChakraBox
        border="1px"
        borderColor="neutral.line"
        borderRadius="12px"
        marginBottom="24px"
        paddingX="18px"
        paddingY="12px"
      >
        <Text marginBottom="12px" variant="h5">
          {goal.pathwayGoal.label}
        </Text>
        <ChakraFlex alignItems="center" gap="12px">
          <InputGroupV2 id={`${TOP_LEVEL_ID}_desc-input-group`}>
            <TextInputV2
              id={`${TOP_LEVEL_ID}_desc-input`}
              onChange={setDescription}
              placeholder="Add context as needed regarding this goal"
              value={description}
            />
          </InputGroupV2>
        </ChakraFlex>
      </ChakraBox>
      <ChakraFlex
        alignItems="center"
        justifyContent="space-between"
        marginTop="24px"
        width="100%"
      >
        <FireflyButton
          id={`${TOP_LEVEL_ID}-cancel`}
          isLoading={isUpdateGoalLoading}
          leftIcon={FAIconName.timesRegular}
          onClick={() => {
            trackEvent(CarePlanEvents.clickOnCancelButtonInEditGoalModal);
            onClose();
          }}
          variant="secondary"
        >
          Cancel
        </FireflyButton>
        <FireflyButton
          id={`${TOP_LEVEL_ID}-save`}
          isDisabled={description === ''}
          isLoading={isUpdateGoalLoading}
          leftIcon={FAIconName.checkRegular}
          onClick={() => {
            trackEvent(CarePlanEvents.clickOnSaveChangesButtonInEditGoalModal);
            updateGoal({
              treatmentEpisodeId,
              goalId: goal.id,
              body: {
                description,
                statusCode: goal.status.code as CarePlanGoalStatus,
              },
            }).then(() => {
              onClose();
            });
          }}
          variant="primary"
        >
          Save changes
        </FireflyButton>
      </ChakraFlex>
    </ModalV2>
  );
};
